import React from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import * as yup from 'yup';
import { PrimaryButton } from '@magoosh/lib/button';
import { RecommendationsFormProps, RecommendationsFormValues, StudyAreas, DegreeTypes } from './types';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import FieldButtonGroup from '@magoosh/lib/formik_inputs/field_button_group';
import { FieldSelect } from '@magoosh/lib/formik_inputs/field_select/template';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import style from '../style.module.scss';
import cx from 'classnames';
import fetch from 'utilities/fetch';
import { paths } from 'config/path_helpers';
import { bugsnagNotify } from 'utilities/bugsnag';

const studyAreas = [
  {
    displayName: 'Science',
    value: StudyAreas.SCIENCE
  },
  {
    displayName: 'Social Science',
    value: StudyAreas.SOCIAL_SCIENCE
  },
  {
    displayName: 'Humanities',
    value: StudyAreas.HUMANITIES
  },
  {
    displayName: 'Business',
    value: StudyAreas.BUSINESS
  },
  {
    displayName: 'Law',
    value: StudyAreas.LAW
  }
];

const degreeTypes = [
  {
    displayName: 'Masters',
    value: DegreeTypes.MASTERS
  },
  {
    displayName: 'Doctorate',
    value: DegreeTypes.DOCTORATE
  },
  {
    displayName: 'Other',
    value: DegreeTypes.OTHER
  }
];

const goalScoreOptions = {
  '130-140': '130-140',
  '140-150': '140-150',
  '150-155': '150-155',
  '155-160': '155-160',
  '160-165': '160-165',
  '165-170': '165-170'
};

const validationSchema = yup.object({
  mathScoreGoal: yup.string().required('Please choose a score goal.'),
  verbalScoreGoal: yup.string().required('Please choose a score goal.'),
  studyArea: yup.string().required('Please select your area of study.'),
  degreeType: yup.string().required('Please select the degree type you want to pursue.'),
  practiceTestFeeling: yup
    .string()
    .required('Please share your thoughts. This will help our tutors offer highly relevant recommendations.')
});

export const RecommendationsForm: React.FC<RecommendationsFormProps> = (props) => {
  const initialValues: RecommendationsFormValues = {
    mathScoreGoal: '',
    verbalScoreGoal: '',
    studyArea: '',
    degreeType: '',
    practiceTestFeeling: ''
  };

  const handleOnSubmit = (
    values: RecommendationsFormValues,
    { setFieldError, setSubmitting }: FormikActions<RecommendationsFormValues>
  ): void => {
    setSubmitting(true);
    fetch(paths.api.mockTestRecommendationRequest(props.mockTestId), {
      method: 'POST',
      body: JSON.stringify({
        recommendationRequest: {
          studentInputs: {
            ...values
          }
        }
      })
    })
      .then(() => {
        props.onComplete();
      })
      .catch((error) => {
        const fieldErrors = error.errors;
        setFieldError('general', fieldErrors._error);
        bugsnagNotify(error);
      })
      .then(() => {
        setSubmitting(false);
      });
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleOnSubmit} validationSchema={validationSchema}>
      {({ errors, isSubmitting, submitForm }) => (
        <Form>
          {(errors as any).general && <Alert bsStyle="danger">{(errors as any).general}</Alert>}
          <Row>
            <Col sm={12} md={6}>
              <FieldSelect
                className="form-control"
                name="mathScoreGoal"
                label="Math Score Goal"
                options={goalScoreOptions}
                placeholder="Choose a range"
                block={true}
              />
            </Col>
            <Col sm={12} md={6}>
              <FieldSelect
                className="form-control"
                name="verbalScoreGoal"
                label="Verbal Score Goal"
                options={goalScoreOptions}
                placeholder="Choose a range"
                block={true}
              />
            </Col>
          </Row>
          <div className='u-margin-T-s'>
            <FieldButtonGroup label="Area of Study" name="studyArea" options={studyAreas} />
            <FieldButtonGroup label="Degree Type" name="degreeType" options={degreeTypes} />
          </div>

          <FieldInput
            name="practiceTestFeeling"
            type="textarea"
            label="How did you feel about your practice test?"
            placeholder="What was easy? What was challenging?"
          />
          <br />
          <PrimaryButton submitting={isSubmitting} onClick={submitForm} block={true}>
            Submit
          </PrimaryButton>
          <p className={cx(style.infoMessage, 'u-margin-T-xs')}>
            <small>
              <FontAwesomeIcon icon={'info-circle'} /> {''}As part of our commitment to student success,
              Magoosh is offering this service at no additional cost to select Premium members.
            </small>
          </p>
        </Form>
      )}
    </Formik>
  );
};

export default RecommendationsForm;
